import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { ONBOARDING_API_CONFIG, OnboardingApiConfigInterface } from '@planone/bau365/data-access-onboarding';
import {
  MOCK_API,
  MockEnvironmentInterface,
  PostMockGenerator,
} from '@planone/bau365/util-api-response-mock-generators';
import { Post } from '@planone/bau365/util-fair-data';
import { map, of } from 'rxjs';

import { PostListRequest, PostResponse } from '../../interfaces/post.interfaces';

@Injectable()
export class DataAccessPostsService {
  constructor(
    private httpClient: HttpClient,
    @Inject(LOCALE_ID) private locale: string,
    @Inject(ONBOARDING_API_CONFIG)
    private onboarding_api_config: OnboardingApiConfigInterface,
    @Inject(MOCK_API) private mockConfig: MockEnvironmentInterface
  ) {}

  private static emptyArrayToUndefined<T>(inputArray: T[] | null | undefined): T[] | undefined {
    if (!inputArray) return undefined;

    const filteredArray = inputArray.filter((value: T) => !!value);

    if (filteredArray.length <= 0) return undefined;

    return filteredArray;
  }

  private static transformPost(post: Post): Post {
    return {
      ...post,
      videos: this.emptyArrayToUndefined(post.videos),
      downloads: this.emptyArrayToUndefined(post.downloads),
      links: this.emptyArrayToUndefined(post.links),
    };
  }

  getPosts(
    fairId: string | undefined = undefined,
    cursor: string | undefined = undefined,
    limit = 18,
    exhibitorId: string | undefined = undefined
  ) {
    const requestParams: PostListRequest = {
      limit: limit,
    };

    if (typeof cursor !== 'undefined') {
      requestParams['cursor'] = cursor;
    }

    if (typeof exhibitorId !== 'undefined') {
      requestParams['exhibitor_id'] = exhibitorId;
    }

    if (this.mockConfig.globally) {
      return of(PostMockGenerator.getPaginatedList(fairId, exhibitorId));
    }

    return this.httpClient
      .get<PostResponse>(`${this.onboarding_api_config.fairDataGatewayUrl}/fairs/${fairId}/posts`, {
        params: { ...requestParams },
      })
      .pipe(
        map((response: PostResponse) => {
          return {
            ...response,
            items: response.items.map((post: Post) => DataAccessPostsService.transformPost(post)),
          };
        })
      );
  }

  getPost(fairId: string, postId: string) {
    if (this.mockConfig.globally) {
      return of(PostMockGenerator.getObject(postId, fairId));
    }

    return this.httpClient
      .get<Post>(`${this.onboarding_api_config.fairDataGatewayUrl}/fairs/${fairId}/posts/${postId}`)
      .pipe(map((post: Post) => DataAccessPostsService.transformPost(post)));
  }

  getLandingPageObjects(ids: string[] | undefined) {
    if (this.mockConfig.globally) {
      return of(PostMockGenerator.getList(ids, undefined));
    }

    return this.httpClient
      .post<Post[]>(`${this.onboarding_api_config.fairDataGatewayUrl}/landing/posts`, ids, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .pipe(map((posts: Post[]) => posts.map((post: Post) => DataAccessPostsService.transformPost(post))));
  }
}
